import { format, formatDistanceToNow, differenceInMinutes } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  CheckCircle2,
  Circle,
  Clock,
  PlayCircle,
  AlertCircle,
  Timer,
  User
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface ProcessRecord {
  id: string;
  status: string;
  started_at: string | null;
  completed_at: string | null;
  created_at: string;
  notes: string | null;
  manufacturing_processes: {
    id: string;
    name: string;
    description: string | null;
    category: string | null;
    estimated_duration: number | null;
  } | null;
  profiles: {
    id: string;
    full_name: string | null;
    email: string;
  } | null;
}

interface ProcessTimelineProps {
  records: ProcessRecord[];
  orderStatus: string;
}

export function ProcessTimeline({ records, orderStatus }: ProcessTimelineProps) {
  const { language } = useLanguage();
  const { userRole } = useAuth();
  const locale = language === "es" ? es : enUS;

  // Solo superadmin, admin y supervisor pueden ver tiempos
  const canViewTimes = ['superadmin', 'admin', 'supervisor'].includes(userRole || '');

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case "in_progress":
        return <PlayCircle className="w-5 h-5 text-blue-500 animate-pulse" />;
      case "paused":
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      default:
        return <Circle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "border-green-500 bg-green-500/10";
      case "in_progress":
        return "border-blue-500 bg-blue-500/10";
      case "paused":
        return "border-orange-500 bg-orange-500/10";
      default:
        return "border-gray-300 bg-gray-100 dark:border-gray-600 dark:bg-gray-800";
    }
  };

  const getLineColor = (status: string) => {
    switch (status) {
      case "completed":
        return "bg-green-500";
      case "in_progress":
        return "bg-blue-500";
      default:
        return "bg-gray-300 dark:bg-gray-600";
    }
  };

  const calculateDuration = (record: ProcessRecord) => {
    if (!record.started_at) return null;

    const start = new Date(record.started_at);
    const end = record.completed_at ? new Date(record.completed_at) : new Date();
    const minutes = differenceInMinutes(end, start);

    if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMins = minutes % 60;
      return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
    }
  };

  const getEstimatedVsActual = (record: ProcessRecord) => {
    if (!record.manufacturing_processes?.estimated_duration || !record.started_at) {
      return null;
    }

    const estimatedMins = record.manufacturing_processes.estimated_duration;
    const start = new Date(record.started_at);
    const end = record.completed_at ? new Date(record.completed_at) : new Date();
    const actualMins = differenceInMinutes(end, start);

    const diff = actualMins - estimatedMins;
    const percentage = Math.round((actualMins / estimatedMins) * 100);

    return {
      estimated: estimatedMins,
      actual: actualMins,
      diff,
      percentage,
      isOver: diff > 0
    };
  };

  // Calculate overall progress
  const completedCount = records.filter(r => r.status === "completed").length;
  const progressPercentage = records.length > 0
    ? Math.round((completedCount / records.length) * 100)
    : 0;

  if (records.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
        <Clock className="w-12 h-12 mb-4 opacity-50" />
        <p>No hay procesos registrados</p>
        <p className="text-sm">Agrega procesos para ver el timeline</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Progress Summary */}
        <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
          <div className="flex items-center gap-4">
            <div className="relative w-16 h-16">
              <svg className="w-16 h-16 transform -rotate-90">
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  className="stroke-muted-foreground/20"
                  strokeWidth="4"
                  fill="none"
                />
                <circle
                  cx="32"
                  cy="32"
                  r="28"
                  className="stroke-primary transition-all duration-500"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray={`${progressPercentage * 1.76} 176`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold">{progressPercentage}%</span>
              </div>
            </div>
            <div>
              <p className="font-medium">Progreso de la Orden</p>
              <p className="text-sm text-muted-foreground">
                {completedCount} de {records.length} procesos completados
              </p>
            </div>
          </div>
          <div className="flex gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              <span>Completado</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500 animate-pulse" />
              <span>En progreso</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-400" />
              <span>Pendiente</span>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="relative">
          {records.map((record, index) => {
            const duration = calculateDuration(record);
            const comparison = getEstimatedVsActual(record);
            const isLast = index === records.length - 1;

            return (
              <div key={record.id} className="relative flex gap-4">
                {/* Timeline Line */}
                <div className="flex flex-col items-center">
                  <div className={cn(
                    "w-10 h-10 rounded-full border-2 flex items-center justify-center z-10",
                    getStatusColor(record.status)
                  )}>
                    {getStatusIcon(record.status)}
                  </div>
                  {!isLast && (
                    <div className={cn(
                      "w-0.5 flex-1 min-h-[60px]",
                      getLineColor(record.status)
                    )} />
                  )}
                </div>

                {/* Content */}
                <div className={cn(
                  "flex-1 pb-8",
                  isLast && "pb-0"
                )}>
                  <div className={cn(
                    "p-4 rounded-lg border transition-all",
                    record.status === "in_progress" && "ring-2 ring-blue-500/50",
                    "hover:shadow-md"
                  )}>
                    {/* Header */}
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-lg">
                          {record.manufacturing_processes?.name || "Proceso"}
                        </h4>
                        {record.manufacturing_processes?.category && (
                          <Badge variant="outline" className="text-xs mt-1">
                            {record.manufacturing_processes.category}
                          </Badge>
                        )}
                      </div>
                      <Badge className={cn(
                        "capitalize",
                        record.status === "completed" && "bg-green-500",
                        record.status === "in_progress" && "bg-blue-500",
                        record.status === "paused" && "bg-orange-500"
                      )}>
                        {record.status === "completed" ? "Completado" :
                         record.status === "in_progress" ? "En Progreso" :
                         record.status === "paused" ? "Pausado" : "Pendiente"}
                      </Badge>
                    </div>

                    {/* Description */}
                    {record.manufacturing_processes?.description && (
                      <p className="text-sm text-muted-foreground mb-3">
                        {record.manufacturing_processes.description}
                      </p>
                    )}

                    {/* Time Info - Solo visible para admin/supervisor */}
                    {canViewTimes && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        {record.started_at && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <PlayCircle className="w-4 h-4" />
                                <span>
                                  {format(new Date(record.started_at), "HH:mm", { locale })}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              Iniciado: {format(new Date(record.started_at), "PPp", { locale })}
                            </TooltipContent>
                          </Tooltip>
                        )}

                        {record.completed_at && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <CheckCircle2 className="w-4 h-4 text-green-500" />
                                <span>
                                  {format(new Date(record.completed_at), "HH:mm", { locale })}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              Completado: {format(new Date(record.completed_at), "PPp", { locale })}
                            </TooltipContent>
                          </Tooltip>
                        )}

                        {duration && (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Timer className="w-4 h-4" />
                            <span>{duration}</span>
                          </div>
                        )}

                        {record.profiles && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <User className="w-4 h-4" />
                                <span className="truncate">
                                  {record.profiles.full_name || record.profiles.email}
                                </span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              Operador: {record.profiles.full_name || record.profiles.email}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    )}

                    {/* Time Comparison - Solo visible para admin/supervisor */}
                    {canViewTimes && comparison && (
                      <div className={cn(
                        "mt-3 p-2 rounded text-xs flex items-center justify-between",
                        comparison.isOver ? "bg-red-500/10 text-red-600" : "bg-green-500/10 text-green-600"
                      )}>
                        <span>
                          Estimado: {comparison.estimated} min | Actual: {comparison.actual} min
                        </span>
                        <span className="font-medium">
                          {comparison.isOver ? "+" : ""}{comparison.diff} min ({comparison.percentage}%)
                        </span>
                      </div>
                    )}

                    {/* Notes */}
                    {record.notes && (
                      <div className="mt-3 p-2 rounded bg-muted/50 text-sm">
                        <p className="text-muted-foreground">{record.notes}</p>
                      </div>
                    )}

                    {/* In Progress Indicator - Solo visible para admin/supervisor */}
                    {canViewTimes && record.status === "in_progress" && (
                      <div className="mt-3 flex items-center gap-2 text-blue-500 text-sm">
                        <div className="w-2 h-2 rounded-full bg-blue-500 animate-ping" />
                        <span>
                          En progreso desde {record.started_at && formatDistanceToNow(new Date(record.started_at), { locale, addSuffix: false })}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
}
